/** Small, white company insignia. All geometry fits inside the ±13px box. */
import type { Graphics } from 'pixi.js';
import type { UnitType } from '../data/units.ts';
import { UF } from '../sim/types.ts';

export const COMPANY_ICONS = [
  'sword', 'shield', 'spear', 'axe', 'hammer', 'scythe',
  'bow', 'crossbow', 'gun', 'horse', 'artillery', 'staff',
  'wings', 'claw', 'swarm', 'missile', 'tools',
] as const;

export type CompanyIcon = typeof COMPANY_ICONS[number];

/** Mobility/crew roles take precedence over the rider's or crew's hand weapon. */
export function companyIconFor(t: UnitType): CompanyIcon {
  const { flags, vis } = t;
  if ((flags & UF.Flying) !== 0 || vis.build === 'flier') return 'wings';
  if ((flags & UF.Cavalry) !== 0 || vis.build === 'cavalry') return 'horse';
  if ((flags & UF.Siege) !== 0 || vis.build === 'siege') return 'artillery';
  if ((flags & UF.Caster) !== 0 || vis.build === 'caster') return 'staff';
  if (vis.build === 'swarm') return 'swarm';
  if ((flags & UF.Villager) !== 0 || vis.build === 'villager') return 'tools';
  if ((flags & UF.Spear) !== 0 || vis.weapon === 'spear') return 'spear';
  switch (vis.weapon) {
    case 'bow': return 'bow';
    case 'crossbow': return 'crossbow';
    case 'gun': return 'gun';
    case 'staff': return 'staff';
    case 'axe': return 'axe';
    case 'hammer': return 'hammer';
    case 'scythe': return 'scythe';
    case 'claw': return 'claw';
    case 'sword': return vis.shield || (flags & UF.Shielded) !== 0 ? 'shield' : 'sword';
    case 'none':
      if ((flags & UF.Ranged) !== 0) return 'missile';
      if ((flags & UF.Sapper) !== 0) return 'tools';
      return vis.build === 'brute' || (flags & UF.Large) !== 0 ? 'claw' : 'sword';
  }
}

const WHITE = 0xffffff;

function fill(g: Graphics, points: number[]): void {
  g.poly(points).fill(WHITE);
}

function stroke(g: Graphics, points: number[], width = 2): void {
  g.moveTo(points[0], points[1]);
  for (let i = 2; i < points.length; i += 2) g.lineTo(points[i], points[i + 1]);
  g.stroke({ color: WHITE, width, cap: 'round', join: 'round' });
}

/** Draw at (0,0), without a background, transform or texture allocation. */
export function drawCompanyIcon(g: Graphics, icon: CompanyIcon): void {
  switch (icon) {
    case 'sword':
      fill(g, [-9, 11, -11, 9, -5, 3, -8, 0, -6, -2, -3, 1, 6, -10, 11, -12, 9, -6, 0, 4, 3, 7, 1, 9, -2, 6]);
      break;
    case 'shield':
      // The shield's notch and pointed foot remain readable at tiny zooms.
      fill(g, [-11, -6, -4, -9, 3, -6, 2, 3, -4, 10, -10, 3]);
      stroke(g, [-4, -5, -4, 5], 1.3);
      fill(g, [6, 11, 6, 5, 3, 5, 3, 3, 6, 3, 6, -7, 8, -12, 10, -7, 10, 3, 12, 3, 12, 5, 9, 5, 9, 11]);
      break;
    case 'spear':
      stroke(g, [-8, 11, 6, -7], 2.6);
      fill(g, [3, -6, 11, -12, 9, -3, 6, -5]);
      stroke(g, [-6, 6, -2, 9], 1.6);
      break;
    case 'axe':
      stroke(g, [-7, 11, 6, -10], 2.8);
      fill(g, [3, -9, 7, -7, 12, -5, 11, 2, 7, 3, 5, -2, 1, -4]);
      fill(g, [2, -8, -3, -10, -7, -8, -4, -3, 0, -4]);
      break;
    case 'hammer':
      stroke(g, [-6, 11, 4, -6], 3);
      fill(g, [-5, -11, 0, -12, 11, -5, 12, -1, 8, 3, 3, 0, -6, -6]);
      break;
    case 'scythe':
      stroke(g, [-5, 11, 2, -9], 2.6);
      stroke(g, [-3, 6, 1, 7], 1.8);
      fill(g, [-9, -11, 0, -12, 8, -8, 12, -2, 11, 4, 7, -3, 2, -6, -8, -8]);
      break;
    case 'bow':
      g.moveTo(-5, -11).bezierCurveTo(8, -8, 8, 8, -5, 11).stroke({ color: WHITE, width: 2.4, cap: 'round' });
      stroke(g, [-5, -10, -5, 10], 1.2);
      stroke(g, [-11, 0, 10, 0], 1.9);
      fill(g, [6, -4, 12, 0, 6, 4, 8, 0]);
      stroke(g, [-10, -3, -7, 0, -10, 3], 1.4);
      break;
    case 'crossbow':
      g.moveTo(-11, -8).quadraticCurveTo(0, 3, 11, -8).stroke({ color: WHITE, width: 2.4, cap: 'round' });
      stroke(g, [-10, -7, 0, -4, 10, -7], 1.3);
      stroke(g, [0, 10, 0, -10], 2.8);
      fill(g, [-3, -7, 0, -12, 3, -7]);
      fill(g, [-2, 4, 3, 4, 2, 11, -2, 11]);
      break;
    case 'gun':
      fill(g, [-12, 6, -7, 3, -4, 5, 7, -6, 10, -5, -1, 7, -4, 7, -8, 12]);
      stroke(g, [-2, 3, 10, -9], 2.8);
      stroke(g, [6, -8, 8, -6], 1.5);
      stroke(g, [-1, 7, 2, 8, 4, 5], 1.4);
      break;
    case 'horse':
      fill(g, [-10, 11, -7, 5, -7, -2, -10, -7, -5, -6, -5, -12, 0, -9, 4, -8, 11, 0, 9, 5, 3, 3, 2, 7, 6, 11]);
      // Transparent cuts keep the insignia usable on every faction colour.
      g.circle(3.5, -4, 1.3).cut();
      fill(g, [-11, -4, -8, -3, -7, 1, -11, 2]);
      break;
    case 'artillery':
      stroke(g, [-10, 7, 10, 7], 2.8);
      g.circle(-7, 9, 3).fill(WHITE);
      g.circle(7, 9, 3).fill(WHITE);
      stroke(g, [-5, 5, 0, -4, 5, 5], 2.6);
      stroke(g, [-8, -7, 9, 1], 3.2);
      fill(g, [-11, -11, -5, -8, -7, -4, -12, -7]);
      stroke(g, [1, -3, 1, 5], 1.5);
      break;
    case 'staff':
      stroke(g, [-6, 11, 4, -6], 2.7);
      fill(g, [2, -12, 8, -11, 10, -6, 6, -2, 1, -5]);
      g.poly([3.5, -9.5, 6.5, -9, 7, -6.5, 4.5, -5.5, 3, -7.5]).cut();
      stroke(g, [-8, -4, -8, 1], 1.6);
      stroke(g, [-10.5, -1.5, -5.5, -1.5], 1.6);
      break;
    case 'wings':
      fill(g, [-1, 8, -5, 5, -11, 5, -8, 2, -12, 0, -9, -1, -12, -7, -7, -5, -9, -11, -3, -7, 0, 1]);
      fill(g, [1, 8, 5, 5, 11, 5, 8, 2, 12, 0, 9, -1, 12, -7, 7, -5, 9, -11, 3, -7, 0, 1]);
      fill(g, [-2, -1, 0, -5, 2, -1, 1, 9, 0, 12, -1, 9]);
      break;
    case 'claw':
      for (const x of [-7, 0, 7]) fill(g, [x - 2, -9, x + 2, -12, x + 1, 1, x - 2, 8, x - 4, 11, x - 1, 0]);
      break;
    case 'swarm':
      for (const [x, y] of [[-7, 3], [7, 3], [0, -6]]) {
        fill(g, [x - 4, y - 4, x, y - 2, x + 4, y - 4, x + 3, y + 3, x, y + 6, x - 3, y + 3]);
        g.circle(x - 1.5, y + 0.5, 0.8).circle(x + 1.5, y + 0.5, 0.8).cut();
      }
      break;
    case 'missile':
      // Generic projectiles suit slingers and spitters without inventing a bow.
      for (const [x, y] of [[-6, -5], [-2, 3], [6, -1]]) {
        stroke(g, [x - 5, y + 6, x + 2, y - 1], 1.8);
        fill(g, [x - 1, y - 1, x + 5, y - 4, x + 2, y + 2]);
      }
      break;
    case 'tools':
      stroke(g, [-8, 11, 6, -10], 2.5);
      fill(g, [-6, -11, 1, -11, 10, -5, 12, 0, 6, -3, 0, -6]);
      stroke(g, [8, 11, -4, -4], 2.2);
      fill(g, [-10, -9, -5, -9, -1, -4, -5, 0, -10, -4]);
      break;
  }
}
